import React, { useEffect, useState } from "react";
import { capitalizeFirstLetter } from "../../../utilz/function";
import Input from "../../../components/input";
import Table from "../../../components/table";
import { I_PurchaseData } from "../types";

interface UserTableProps {
  selectedStatus?: string;
  handleStatusChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  data: I_PurchaseData[] | I_PurchaseData;
}

const PurchaseOrderTable: React.FC<UserTableProps> = ({
  selectedStatus,
  data,
}) => {
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");

  const columns = [
    { header: "Item Name", accessor: "name" },
    { header: "Quantity", accessor: "quantity" },
    { header: "Unit Price", accessor: "unitPrice" },
    { header: "Subtotal", accessor: "subTotal" },
  ];

  // Extract items data from the purchase order object
  const getItemsData = (purchaseOrder: I_PurchaseData) => {
    if (!purchaseOrder.Items || purchaseOrder.Items.length === 0) {
      return [];
    }

    return purchaseOrder.Items.map((item) => ({
      id: item._id,
      name: item.name || item.name,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      subTotal: item.subTotal,
      status: purchaseOrder.status,
    }));
  };

  useEffect(() => {
    try {
      let processedData: I_PurchaseData[] = [];

      if (Array.isArray(data)) {
        processedData = data;
      } else if (data && typeof data === "object") {
        processedData = [data as I_PurchaseData];
      }

      const allItems = processedData.flatMap(getItemsData);

      const filtered = allItems.filter((item) => {
        const statusMatch = !selectedStatus || item?.status === selectedStatus;

        const searchMatch =
          !searchQuery ||
          item?.name?.toLowerCase().includes(searchQuery?.toLowerCase());
        const typeMatch = !selectedType || item?.status === selectedType;
        return statusMatch && searchMatch && typeMatch;
      });

      setFilteredData(filtered);
    } catch (error) {
      console.error("Error processing purchase order data:", error);
      setFilteredData([]);
    }
  }, [data, selectedStatus, searchQuery, selectedType]);

  const formatCurrency = (amount: number) => {
    return `₦${Number(amount).toLocaleString()}`;
  };

  const combinedData = filteredData.map((item) => ({
    ...item,
    name: (
      <div>
        <div>{capitalizeFirstLetter(item?.name)}</div>
      </div>
    ),
    quantity: (
      <div>
        <div>{item?.quantity}</div>
      </div>
    ),
    unitPrice: (
      <div>
        <div>{formatCurrency(item?.unitPrice)}</div>
      </div>
    ),
    subTotal: (
      <div>
        <div>{formatCurrency(item?.subTotal)}</div>
      </div>
    ),
  }));

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="py-3 bg-white rounded-3xl shadow border w-full border-[#C9C9C9] pt-5">
      <div className="md:flex justify-between px-5">
        <p className="text-[20px] font-bold">Purchase Order Items</p>
        <div className="md:flex gap-2 items-center md:mt-0 mt-2">
          <Input
            name="search"
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full"
            placeholder="Search items"
            icon="searchIcon"
          />
        </div>
      </div>

      {combinedData.length > 0 ? (
        <Table columns={columns} data={combinedData} selectedUserId={null} />
      ) : (
        <div className="opacity-80 mt-10 text-[14px] font-semibold text-center text-[#4E4E4E] mx-auto py-8">
          No items found matching your criteria
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderTable;
